/**
 * Bootstrap hook for prefetching Leads data after login/home mount
 * 
 * Goal: Prefetch the first page of Leads data in the background so that
 * when the user clicks on the Leads menu item, the data is already in cache.
 * 
 * This provides a "big CRM" experience where the Leads page appears almost instantly.
 * 
 * Usage:
 * ```tsx
 * const { company } = useParams<{ company: string }>();
 * const { client } = useClient();
 * 
 * useBootstrapLeadsData({
 *   companyId: company,
 *   enabled: !!client && !!company,
 * });
 * ```
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { leadKeys } from "../lib/queryKeys";
import rpcLeads from "../api/rpc/rpcLeads";
import { DEFAULT_LEADS_QUERY_PARAMS } from "./leadsQueryDefaults";

type UseBootstrapLeadsDataProps = {
  companyId: string | undefined;
  enabled?: boolean; // default true
};

export function useBootstrapLeadsData({ 
  companyId, 
  enabled = true 
}: UseBootstrapLeadsDataProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (!companyId) return;

    // Use the EXACT same default params as the Leads page initial query
    // This ensures the query keys match and cached data is immediately available
    const prefetchParams = { ...DEFAULT_LEADS_QUERY_PARAMS };
    const queryKey = leadKeys.list(companyId, prefetchParams);

    // Check if data already exists in cache (before prefetching)
    const existingData = queryClient.getQueryData(queryKey);
    if (existingData) {
      return;
    }

    // Prefetch in background - don't await, let it run silently
    queryClient.prefetchQuery({
      queryKey,
      queryFn: async () => {
        const res = await rpcLeads.list({
          q: prefetchParams.search || undefined,
          status: prefetchParams.status,
          managerId: prefetchParams.managerId,
          rating: prefetchParams.rating,
          source: prefetchParams.source,
          leadNum: prefetchParams.leadNum,
          firstName: prefetchParams.firstName,
          page: prefetchParams.page,
          limit: prefetchParams.limit,
        });

        if (!res.data?.ok) {
          throw new Error(res.data?.error || 'Failed to get leads');
        }

        const leadsData = res.data.data || {};
        const items = Array.isArray(leadsData?.items) ? leadsData.items : [];
        const total = Number(leadsData?.total || items.length);

        return {
          data: items,
          pagination: {
            page: prefetchParams.page || DEFAULT_LEADS_QUERY_PARAMS.page || 1,
            limit: prefetchParams.limit || DEFAULT_LEADS_QUERY_PARAMS.limit || 25,
            total,
            pages: Math.ceil(total / (prefetchParams.limit || DEFAULT_LEADS_QUERY_PARAMS.limit || 25)),
            hasMore: (prefetchParams.page || 1) * (prefetchParams.limit || 25) < total,
          },
        };
      },
      // Use same caching strategy as the actual query (must match useLeadsQuery)
      // Use queryOptions.heavyTable preset: 2min stale, 60min cache
      staleTime: 2 * 60 * 1000, // 2 minutes - matches queryOptions.heavyTable
      gcTime: 60 * 60 * 1000, // 60 minutes - matches queryOptions.heavyTable
    }).catch((error) => {
      // Silently handle errors - prefetch failures shouldn't break the app
    });
  }, [companyId, enabled, queryClient]);
}

